/**
 * Compact single-choice filter. ``null`` is the "All" option, which
 * every control has, so a filter can always be cleared where it was set.
 */
import { cn } from "@/lib/utils";

export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  /** Names the group for screen readers; not shown. */
  label: string;
  value: T | undefined;
  options: readonly T[];
  onChange: (next: T | null) => void;
}) {
  const items: ReadonlyArray<{ key: T | null; text: string }> = [
    { key: null, text: "All" },
    ...options.map((o) => ({ key: o, text: o })),
  ];
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-lg border bg-card p-0.5 shadow-sm">
      {items.map((item) => {
        const active = (value ?? null) === item.key;
        return (
          <button
            key={item.text}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(item.key)}
            className={cn(
              "h-7 rounded-md px-3 text-xs font-medium capitalize transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.text}
          </button>
        );
      })}
    </div>
  );
}
